import React from 'react';
import { CheckCircle, AlertTriangle, XCircle, Info, X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Reusable Notification Component
 * Displays success, error, warning, and info messages with consistent styling
 */
const Notification = ({
  type = 'info',
  title,
  message,
  onClose,
  extraInfo,
  warnings = [],
  className = ''
}) => {
  const { colors } = useTheme();

  const config = {
    success: {
      bgColor: 'bg-gradient-to-r from-green-900/50 to-green-800/50',
      borderColor: 'border-green-600',
      iconColor: 'text-green-400',
      titleColor: 'text-green-200',
      messageColor: 'text-green-300',
      Icon: CheckCircle
    },
    error: {
      bgColor: 'bg-gradient-to-r from-red-900/50 to-red-800/50',
      borderColor: 'border-red-600',
      iconColor: 'text-red-400',
      titleColor: 'text-red-200',
      messageColor: 'text-red-300',
      Icon: XCircle
    },
    warning: {
      bgColor: 'bg-gradient-to-r from-yellow-900/50 to-yellow-800/50',
      borderColor: 'border-yellow-600',
      iconColor: 'text-yellow-400',
      titleColor: 'text-yellow-200',
      messageColor: 'text-yellow-300',
      Icon: AlertTriangle
    },
    info: {
      bgColor: 'bg-gradient-to-r from-blue-900/50 to-blue-800/50',
      borderColor: 'border-blue-600',
      iconColor: 'text-blue-400',
      titleColor: 'text-blue-200',
      messageColor: 'text-blue-300',
      Icon: Info
    }
  };

  const { bgColor, borderColor, iconColor, titleColor, messageColor, Icon } = config[type];

  return (
    <div className={`mb-6 p-4 ${bgColor} ${borderColor} rounded-2xl shadow-sm backdrop-blur-sm border ${className}`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        <div className="ml-3 flex-1">
          {title && <h3 className={`text-sm font-semibold ${titleColor}`}>{title}</h3>}
          {message && <p className={`${title ? 'mt-1' : ''} text-sm ${messageColor}`}>{message}</p>}

          {extraInfo && (
            <p className={`mt-2 text-sm ${messageColor} italic`}>{extraInfo}</p>
          )}

          {warnings.length > 0 && (
            <div className="mt-3">
              <p className="text-sm font-semibold text-yellow-200">Issues:</p>
              <ul className="mt-1 list-disc list-inside text-sm text-yellow-300 space-y-1">
                {warnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className={`ml-3 ${iconColor} hover:opacity-75 hover:scale-110 transition-transform`}
            aria-label="Close notification"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default Notification;
